// import { Spinner } from "reactstrap";
 
import Spinner from 'react-bootstrap/Spinner';
import { BASE_URL } from "../Constants";

// Encryption key for user data (obfuscated to prevent casual tampering)
// Note: Client-side encryption is not 100% secure, but prevents casual modification
const KY = 'EYiub[K2hXWT![ao8@k?CP0o_s.DMi}';

// Simple encryption function for user data
const encryptUserData = (data) => {
  try {
    if (!data) {
      return null;
    }
    const jsonString = JSON.stringify(data);
    
    // Use a simple XOR cipher with the key
    let encrypted = '';
    for (let i = 0; i < jsonString.length; i++) {
      const keyChar = KY[i % KY.length];
      encrypted += String.fromCharCode(jsonString.charCodeAt(i) ^ keyChar.charCodeAt(0));
    }
    
    // Base64 encode for safe storage
    // Use encodeURIComponent to handle all characters safely, then base64
    try {
      const uriEncoded = encodeURIComponent(encrypted);
      const base64 = btoa(uriEncoded);
      return base64;
    } catch (btoaError) {
      // Fallback: use a simpler encoding if btoa fails
      return btoa(encrypted);
    }
  } catch (error) {
    return null;
  }
};

// Decryption function for user data
const decryptUserData = (encryptedData) => {
  try {
    if (!encryptedData) {
      return null;
    }
    
    // Base64 decode
    let encrypted;
    try {
      // Decode base64 first
      const base64Decoded = atob(encryptedData);
      // Then decode URI component to get the original encrypted string
      try {
        encrypted = decodeURIComponent(base64Decoded);
      } catch (e) {
        // If decodeURIComponent fails, use the base64 decoded string directly
        encrypted = base64Decoded;
      }
    } catch (e) {
      // Try to parse as plain JSON (for backward compatibility with unencrypted data)
      try {
        return JSON.parse(encryptedData);
      } catch (parseError) {
        return null;
      }
    }
    
    // XOR decrypt
    let decrypted = '';
    for (let i = 0; i < encrypted.length; i++) {
      const keyChar = KY[i % KY.length];
      decrypted += String.fromCharCode(encrypted.charCodeAt(i) ^ keyChar.charCodeAt(0));
    }
    
    // Try to parse the decrypted JSON directly
    try {
      const parsed = JSON.parse(decrypted);
      return parsed;
    } catch (parseError) {
      // If it's a control character error, the data might be corrupted
      // Clear the corrupted data and return null so user can log in again
      if (parseError.message.includes('Bad control character') || parseError.message.includes('control character')) {
        localStorage.removeItem("user-data");
        localStorage.removeItem("user-photo");
        localStorage.removeItem("access-token");
        localStorage.removeItem("refresh-token");
        localStorage.removeItem("token-expires-at");
        return null;
      }
      
      // Re-throw to be caught by outer catch
      throw parseError;
    }
  } catch (error) {
    // Try to parse as plain JSON (for backward compatibility with unencrypted data)
    try {
      return JSON.parse(encryptedData);
    } catch (e) {
      return null;
    }
  }
};

const USER_PHOTO_STORAGE_KEY = "user-photo";

const USER_PHOTO_FIELDS = [
  "photo",
  "image",
  "avatar",
  "picture",
  "profile_picture",
  "photo_url",
];

export const extractUserPhoto = (userData) => {
  if (!userData || typeof userData !== "object") {
    return null;
  }
  for (const field of USER_PHOTO_FIELDS) {
    if (userData[field]) {
      return userData[field];
    }
  }
  return null;
};

export const stripUserPhotoFields = (userData) => {
  if (!userData || typeof userData !== "object") {
    return userData;
  }
  const stripped = { ...userData };
  for (const field of USER_PHOTO_FIELDS) {
    delete stripped[field];
  }
  return stripped;
};

export const setUserPhoto = (photo) => {
  try {
    if (!photo) {
      localStorage.removeItem(USER_PHOTO_STORAGE_KEY);
      return true;
    }
    const encrypted = encryptUserData({ photo });
    if (encrypted) {
      localStorage.setItem(USER_PHOTO_STORAGE_KEY, encrypted);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const getUserPhoto = () => {
  try {
    const encrypted = localStorage.getItem(USER_PHOTO_STORAGE_KEY);
    if (!encrypted) {
      return null;
    }
    const data = decryptUserData(encrypted);
    return data?.photo ?? null;
  } catch (error) {
    return null;
  }
};

export const attachUserPhoto = (user) => {
  if (!user) {
    return user;
  }
  const photo = getUserPhoto() ?? extractUserPhoto(user) ?? null;
  if (!photo) {
    return user;
  }
  return { ...user, photo };
};

const persistEncryptedUserDataOnly = (userData) => {
  const encrypted = encryptUserData(userData);
  if (encrypted) {
    localStorage.setItem("user-data", encrypted);
    return true;
  }
  return false;
};

// Helper functions to store/retrieve encrypted user data
export const setEncryptedUserData = (userData) => {
  try {
    if (!userData) {
      return false;
    }
    const photo = extractUserPhoto(userData);
    setUserPhoto(photo);
    const withoutPhoto = stripUserPhotoFields(userData);
    return persistEncryptedUserDataOnly(withoutPhoto);
  } catch (error) {
    return false;
  }
};

export const getEncryptedUserData = () => {
  try {
    const encrypted = localStorage.getItem("user-data");
    if (!encrypted) {
      return null;
    }

    let decrypted = decryptUserData(encrypted);
    if (!decrypted) {
      return null;
    }

    const embeddedPhoto = extractUserPhoto(decrypted);
    if (embeddedPhoto) {
      if (!getUserPhoto()) {
        setUserPhoto(embeddedPhoto);
      }
      decrypted = stripUserPhotoFields(decrypted);
      persistEncryptedUserDataOnly(decrypted);
    }

    return decrypted;
  } catch (error) {
    // Clear potentially corrupted data
    localStorage.removeItem("user-data");
    localStorage.removeItem(USER_PHOTO_STORAGE_KEY);
    localStorage.removeItem("access-token");
    localStorage.removeItem("refresh-token");
    localStorage.removeItem("token-expires-at");
    return null;
  }
};

// Helper functions to store/retrieve encrypted access token
export const setEncryptedAccessToken = (token) => {
  try {
    if (!token) {
      return false;
    }
    // Encrypt the token (it's a string, so we wrap it in an object for consistency)
    const encrypted = encryptUserData({ token: token });
    if (encrypted) {
      localStorage.setItem("access-token", encrypted);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const getEncryptedAccessToken = () => {
  try {
    const encrypted = localStorage.getItem("access-token");
    if (!encrypted) {
      return null;
    }
    
    // Try to decrypt it (it should be encrypted as { token: "..." })
    try {
      const decrypted = decryptUserData(encrypted);
      if (decrypted && decrypted.token) {
        return decrypted.token;
      }
      // If it's not in the expected format, try parsing as plain string (backward compatibility)
      if (typeof decrypted === 'string') {
        return decrypted;
      }
    } catch (e) {
      // If decryption fails, try as plain string (backward compatibility with unencrypted tokens)
      return encrypted;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

export const setEncryptedRefreshToken = (token) => {
  try {
    if (!token) {
      return false;
    }
    const encrypted = encryptUserData({ token: token });
    if (encrypted) {
      localStorage.setItem("refresh-token", encrypted);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const getEncryptedRefreshToken = () => {
  try {
    const encrypted = localStorage.getItem("refresh-token");
    if (!encrypted) {
      return null;
    }
    try {
      const decrypted = decryptUserData(encrypted);
      if (decrypted && decrypted.token) {
        return decrypted.token;
      }
      if (typeof decrypted === "string") {
        return decrypted;
      }
    } catch (e) {
      return encrypted;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const setTokenExpiresAt = (expiresInSeconds) => {
  if (expiresInSeconds == null || Number.isNaN(Number(expiresInSeconds))) {
    return false;
  }
  localStorage.setItem(
    "token-expires-at",
    String(Date.now() + Number(expiresInSeconds) * 1000)
  );
  return true;
};

export const setTokenExpiresAtFromJwt = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }
  localStorage.setItem("token-expires-at", String(Number(payload.exp) * 1000));
  return true;
};

export const getTokenExpiresAt = () =>
  Number(localStorage.getItem("token-expires-at") || 0);

export const setAuthSession = ({ token, refresh_token, expires_in, user }) => {
  let ok = true;
  if (token) {
    ok = setEncryptedAccessToken(token) && ok;
  }
  if (refresh_token) {
    ok = setEncryptedRefreshToken(refresh_token) && ok;
  }
  if (expires_in != null) {
    ok = setTokenExpiresAt(expires_in) && ok;
  } else if (token) {
    setTokenExpiresAtFromJwt(token);
  }
  if (user) {
    ok = setEncryptedUserData(user) && ok;
  }
  return ok;
};

export const clearAuthSession = () => {
  localStorage.removeItem("access-token");
  localStorage.removeItem("refresh-token");
  localStorage.removeItem("token-expires-at");
  localStorage.removeItem("user-data");
  localStorage.removeItem(USER_PHOTO_STORAGE_KEY);
  localStorage.removeItem("app_mode");
  localStorage.removeItem("permissions");
  localStorage.removeItem("company_info");
  localStorage.removeItem("setting");
};

export const applyAuthBootstrapData = (data, { token } = {}) => {
  if (!data) {
    return false;
  }

  let ok = true;
  if (token) {
    ok = setEncryptedAccessToken(token) && ok;
  }
  if (data.refresh_token) {
    ok = setEncryptedRefreshToken(data.refresh_token) && ok;
  }
  if (data.expires_in != null) {
    ok = setTokenExpiresAt(data.expires_in) && ok;
  } else if (token) {
    setTokenExpiresAtFromJwt(token);
  }
  if (data.user) {
    ok = setEncryptedUserData(data.user) && ok;
  }

  if (Array.isArray(data.permissions)) {
    localStorage.setItem("permissions", JSON.stringify(data.permissions));
  }
  if (data.company_info != null) {
    localStorage.setItem("company_info", JSON.stringify(data.company_info));
  }
  if (data.setting != null) {
    localStorage.setItem("setting", JSON.stringify(data.setting));
  }
  if (data.app_mode != null) {
    localStorage.setItem("app_mode", JSON.stringify(data.app_mode));
  }

  return ok && Boolean(data.user);
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const looksLikeUuid = (value) => UUID_RE.test(String(value || "").trim());

/** Merge stored user with JWT claims (auth/me may omit fields that exist on the token). */
export const mergeUserWithTokenClaims = (user) => {
  const claims = decodeJwtPayload(getEncryptedAccessToken()) || {};
  const claimUser =
    claims.user && typeof claims.user === "object" ? claims.user : claims;
  const base = user && typeof user === "object" ? user : {};
  return { ...claimUser, ...base };
};

/**
 * Body for POST hrm/list-staff-details — accepts staff_code (SOM116) or staff_id (UUID).
 */
export const getListStaffDetailsRequestBody = (user) => {
  const enriched = mergeUserWithTokenClaims(user);
  if (!enriched || typeof enriched !== "object") {
    return null;
  }

  for (const value of [
    enriched.staff_code,
    enriched.staff?.staff_code,
    enriched.employee_code,
  ]) {
    if (value != null && String(value).trim()) {
      return { staff_code: String(value).trim() };
    }
  }

  for (const value of [enriched.staff_id, enriched.staff?.staff_id]) {
    if (value == null || !String(value).trim()) {
      continue;
    }
    const text = String(value).trim();
    if (looksLikeUuid(text)) {
      return { staff_id: text };
    }
    return { staff_code: text };
  }

  for (const value of [
    enriched.staff_account_id,
    enriched.staff?.id,
    enriched.employee_id,
  ]) {
    if (value != null && String(value).trim()) {
      return { staff_id: String(value).trim() };
    }
  }

  return null;
};

/** Display staff code for APIs expecting staff_code (e.g. SOM116). */
export const getLoggedInStaffCode = (user) => {
  const body = getListStaffDetailsRequestBody(user);
  if (!body) {
    return null;
  }
  if (body.staff_code) {
    return body.staff_code;
  }
  return body.staff_id && !looksLikeUuid(body.staff_id) ? body.staff_id : null;
};

/** Internal staff account id for leave/toil submissions. */
export const getLoggedInStaffAccountId = (user) => {
  const enriched = mergeUserWithTokenClaims(user);
  if (!enriched) {
    return null;
  }
  const candidates = [
    enriched.staff_account_id,
    enriched.staff?.id,
    looksLikeUuid(enriched.staff_id) ? enriched.staff_id : null,
    looksLikeUuid(enriched.employee_id) ? enriched.employee_id : null,
    enriched.id,
  ];
  for (const value of candidates) {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
};

export const hasLoggedInStaffProfile = (user) =>
  Boolean(getListStaffDetailsRequestBody(user));

export const getLoggedInUserId = (user) => {
  const enriched = mergeUserWithTokenClaims(user);
  if (!enriched) {
    return null;
  }
  const id = enriched.id ?? enriched.user_id ?? enriched.value;
  return id != null && String(id).trim() !== "" ? String(id).trim() : null;
};

export const isSameUserId = (left, right) => {
  if (left == null || right == null) {
    return false;
  }
  return String(left).trim() === String(right).trim();
};

const parseMaybeJsonArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Normalize leave document list from API / navigation state. */
export const normalizeLeaveDocuments = (leave) => {
  if (!leave || typeof leave !== "object") {
    return [];
  }

  let docs =
    leave.leave_documents ??
    leave.documents ??
    leave.leave_files ??
    leave.document_files ??
    leave.files;

  docs = parseMaybeJsonArray(docs);

  if (!docs.length && leave.document_files && typeof leave.document_files === "object") {
    docs = Object.values(leave.document_files);
  }

  if (!docs.length && leave.documents && typeof leave.documents === "object" && !Array.isArray(leave.documents)) {
    docs = Object.values(leave.documents);
  }

  return docs.filter(
    (doc) =>
      doc &&
      (doc.view_url ||
        doc.download_url ||
        doc.link ||
        doc.file_path ||
        doc.path ||
        doc.file_name ||
        doc.name ||
        doc.document_name ||
        doc.id)
  );
};

export const getLeaveDocumentLabel = (doc) =>
  doc?.name || doc?.file_name || doc?.document_name || doc?.title || "Document";

export const normalizeLeaveRecord = (apiData, routeState = null) => {
  const routeLeave =
    routeState?.leave && typeof routeState.leave === "object" ? routeState.leave : routeState;
  const apiLeave =
    apiData?.leave && typeof apiData.leave === "object" ? apiData.leave : apiData;

  const merged = {
    ...(routeLeave && typeof routeLeave === "object" ? routeLeave : {}),
    ...(apiLeave && typeof apiLeave === "object" ? apiLeave : {}),
  };

  if (apiData?.leave_documents != null) {
    merged.leave_documents = apiData.leave_documents;
  }

  return {
    ...merged,
    leave_documents: normalizeLeaveDocuments(merged),
  };
};

/** Leave-details API may return view_url on the app host; file serving lives on BASE_URL. */
const rewriteLeaveDocumentApiUrl = (url) => {
  if (!url) {
    return null;
  }

  const trimmed = String(url).trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;

    if (path.startsWith("/api/view/") || path.startsWith("/api/downloads/")) {
      return `${BASE_URL}${path}`;
    }
  } catch {
    // Not an absolute URL; handled by callers.
  }

  return trimmed;
};

export const buildLeaveDocumentViewUrl = (doc) => {
  const directUrl = doc?.view_url || doc?.download_url;
  if (directUrl && String(directUrl).trim()) {
    return rewriteLeaveDocumentApiUrl(String(directUrl).trim());
  }

  const link = doc?.link || doc?.file_path || doc?.path;
  if (!link) {
    return null;
  }
  if (String(link).startsWith("http")) {
    return rewriteLeaveDocumentApiUrl(String(link));
  }
  return `${BASE_URL}/api/view/${String(link).replace(/^\//, "")}`;
};

export const openAuthenticatedDocument = async (doc) => {
  const url = buildLeaveDocumentViewUrl(doc);
  if (!url) {
    throw new Error("Document link is missing");
  }

  const token = getEncryptedAccessToken();
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error("Unable to open document");
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = getLeaveDocumentLabel(doc);
    anchor.click();
  }
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

export const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

export const normalizeUserFromAuth = (token, explicitUser = null) => {
  if (explicitUser && typeof explicitUser === "object") {
    return explicitUser;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  if (payload.user && typeof payload.user === "object") {
    return payload.user;
  }

  const userId = payload.user_id ?? payload.id ?? payload.sub;
  if (userId == null) {
    return null;
  }

  return {
    ...payload,
    user_id: payload.user_id ?? userId,
    id: payload.id ?? userId,
  };
};

export const parseJsonParam = (value) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(value));
  } catch (e) {
    try {
      return JSON.parse(value);
    } catch (e2) {
      return null;
    }
  }
};

// Helper function to validate redirect URLs
export const isValidRedirectUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  // Remove leading/trailing slashes and normalize
  const normalizedUrl = url.trim();
  
  // Invalid URLs to never redirect to
  const invalidUrls = [
    '/login',
    '/auth/callback',
    '/reset-password',
    '/update-password',
    '/error/access-denied',
    '/operation/result',
    '/',
    '',
  ];
  
  // Check if URL is in invalid list
  if (invalidUrls.includes(normalizedUrl)) {
    return false;
  }
  
  // Check if URL starts with invalid patterns (exact matches only for specific routes)
  const invalidPatterns = [
    '/login',
    '/auth/callback',
    '/reset-password',
    '/update-password',
  ];
  
  for (const pattern of invalidPatterns) {
    // Only reject if it's an exact match or starts with the pattern followed by / or end of string
    if (normalizedUrl === pattern || normalizedUrl.startsWith(pattern + '/')) {
      return false;
    }
  }
  
  // Reject error pages
  if (normalizedUrl.startsWith('/error/')) {
    return false;
  }
  
  // URL should start with / and be a valid path
  if (!normalizedUrl.startsWith('/')) {
    return false;
  }
  
  // URL should not contain protocol or domain
  if (normalizedUrl.includes('http://') || normalizedUrl.includes('https://') || normalizedUrl.includes('://')) {
    return false;
  }
  
  // If we get here, it's a potentially valid route - allow it
  // The ProtectedRoute component will handle permission checks
  return true;
};

// Helper function to get safe redirect URL
export const getSafeRedirectUrl = (redirectUrl) => {
  const isValid = isValidRedirectUrl(redirectUrl);
  if (isValid) {
    return redirectUrl;
  }
  // Default to dashboard if redirect URL is invalid
  return '/dashboard';
};

export const getCurrentReturnPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

export const isAuthenticatedSession = () =>
  Boolean(getEncryptedAccessToken() && getEncryptedUserData());

export const saveRedirectUrlIfValid = (returnPath) => {
  if (returnPath && isValidRedirectUrl(returnPath)) {
    localStorage.setItem("redirectUrl", returnPath);
    return true;
  }
  return false;
};

export const saveRedirectUrlAndGoToLogin = (returnPath) => {
  saveRedirectUrlIfValid(returnPath || getCurrentReturnPath());
  clearAuthSession();
  window.location.href = "/login";
};

function randomString(length, chars) {
    var result = '';
    for (var i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
    return result;
}
const getUniqueString = (length) => {
    let chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    var result = '';
    for (var i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
    return result;
}; const localizeData = (key, data) => {
    window.localStorage.setItem(key, JSON.stringify(data));
    return null;
};
const getLocalizeData = async (key) => {
    try {
        let lo = window.localStorage.getItem(key);
        // log("At local");
        // log(lo);
        return JSON.parse(lo);
    } catch (e) {
        return JSON.parse([]);
    }

};
const EbmsLoading = (SpinnerStatus) => {
    let displayState = "none";
    let displayClass = "visually-hidden";
    if (SpinnerStatus["data"] == true) {
        displayState = "block";
        displayClass = "";
    } else {
        displayState = "none";
        displayClass = "visually-hidden";
    }
    return (
        <div className={displayClass} style={{ display: { displayState } }}>
            {/* <Spinner animation="border" className={displayClass} role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner> */}
            <Spinner animation="grow" variant="primary" />
            <Spinner animation="grow" variant="secondary" />
            <Spinner animation="grow" variant="success" />
            <Spinner animation="grow" variant="danger" />
            <Spinner animation="grow" variant="warning" />
            <Spinner animation="grow" variant="info" />
            <Spinner animation="grow" variant="light" />
            <Spinner animation="grow" variant="dark" />
        </div>
    );
}
const resetLocalizeData = async (key) => {
    return window.localStorage.removeItem(key);
};
const log = async (title = "", data) => {
    return data;
};

const isObjectEmpty = (obj, key = null) => {
    // log("Check if empty");
    // log("KEY:: "+key);
    // log(obj);
    try {
        if(JSON.stringify(obj) === '{}'){
            return true;  
        }
        if (obj === null || obj === undefined) {
            return true;
        }
        
        // if (key === null || key===undefined) {
        //     return true;
        // }
        let count = 0;
        if (key === null) {
            if (obj === null || obj === undefined) {
                return true;
            } else {
                for (var k in obj) {
                    count++;
                    break;
                }
                return count === 0;
            }
        } else {
            if (obj[key] === undefined || obj[key] === null)
                return true;
            else
                return false;
        }
    } catch (e) {
        return true;
    }

};


export {
    localizeData,
    getLocalizeData,
    resetLocalizeData,
    isObjectEmpty,
    log,
    //modules
    EbmsLoading,
    getUniqueString
    // isValidRedirectUrl and getSafeRedirectUrl are already exported inline above
};